import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { DsButton } from "../../../design-system";
import {
  applyMatchedStructuresForEmployees,
  loadStructureApplyContext,
} from "../create-employee/applyMatchedSalaryStructure";
import StructureApplyAlerts from "../create-employee/StructureApplyAlerts";
import { structureRequiresManualInput } from "../../../utils/applyEmployeeStructureFromTemplate";

/**
 * Bulk-save salary structures for employees with CTC but no first map.
 * 1) Try assignment match (Auto from CTC).
 * 2) For no_structure leftovers, admin picks one company template (still no broken formulas).
 */
export default function ApplyAutoStructuresModal({
  employees: employeesProp = [],
  onClose,
  title = "Apply salary structures",
}) {
  const [employees] = useState(employeesProp);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: employees.length });
  const [applied, setApplied] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [phase, setPhase] = useState("ready"); // ready | result | fallback
  const [templates, setTemplates] = useState([]);
  const [pickedId, setPickedId] = useState("");
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const unmatched = useMemo(
    () => alerts.filter((a) => a.reason === "no_structure"),
    [alerts]
  );
  const otherAlerts = useMemo(
    () => alerts.filter((a) => a.reason !== "no_structure"),
    [alerts]
  );

  const autoTemplates = useMemo(
    () =>
      (templates || []).filter(
        (s) => s && s.active !== false && !structureRequiresManualInput(s)
      ),
    [templates]
  );
  const manualTemplates = useMemo(
    () =>
      (templates || []).filter(
        (s) => s && s.active !== false && structureRequiresManualInput(s)
      ),
    [templates]
  );

  useEffect(() => {
    let cancelled = false;
    setTemplatesLoading(true);
    loadStructureApplyContext()
      .then((ctx) => {
        if (!cancelled) setTemplates(ctx.structures || []);
      })
      .catch(() => {
        if (!cancelled) setTemplates([]);
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const close = () => onClose?.(applied.length > 0);

  const mergeResult = (nextApplied, nextAlerts) => {
    setApplied((prev) => {
      const seen = new Set(prev.map((a) => Number(a.employee?.id)));
      const merged = [...prev];
      for (const a of nextApplied) {
        const id = Number(a.employee?.id);
        if (!seen.has(id)) {
          seen.add(id);
          merged.push(a);
        }
      }
      return merged;
    });
    setAlerts(nextAlerts);
  };

  const runMatched = async () => {
    setBusy(true);
    try {
      const res = await applyMatchedStructuresForEmployees(employees, {
        onProgress: (done, total) => setProgress({ done, total }),
      });
      mergeResult(res.applied, res.alerts);
      const stillUnmatched = res.alerts.filter((a) => a.reason === "no_structure");
      if (stillUnmatched.length) {
        setPhase("fallback");
        setPickedId("");
      } else {
        setPhase("result");
      }
    } finally {
      setBusy(false);
    }
  };

  const runFallback = async () => {
    const structure = templates.find((s) => String(s.id) === String(pickedId));
    if (!structure) return;
    if (structureRequiresManualInput(structure)) {
      setAlerts((prev) => [
        ...prev.filter((a) => a.reason !== "no_structure"),
        ...unmatched.map((a) => ({
          ...a,
          reason: "manual_amounts_required",
          pendingManual: true,
          message:
            "Selected structure is Manual — use Payroll → Manual Component Import (or Profile → Salary Structure) with component amounts. CTC alone cannot build Manual breakdown.",
        })),
      ]);
      setPhase("result");
      return;
    }
    const targets = unmatched.map((a) => a.employee).filter((e) => e?.id);
    if (!targets.length) {
      setPhase("result");
      return;
    }
    setBusy(true);
    try {
      const res = await applyMatchedStructuresForEmployees(targets, {
        structureOverride: structure,
        onProgress: (done, total) => setProgress({ done, total }),
      });
      mergeResult(res.applied, [...otherAlerts, ...res.alerts]);
      setPhase("result");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]">
        <div className="flex items-start justify-between gap-4 border-b border-[hsl(var(--ds-border))] px-6 py-4">
          <div>
            <h2 className="ds-h3 text-[hsl(var(--ds-fg))]">{title}</h2>
            <p className="ds-caption mt-1 text-[hsl(var(--ds-muted-fg))]">
              {employees.length} employee(s) have CTC but no saved first salary-structure map.
              We apply matching Auto (Formula) structures from Profile CTC (effective = joining
              date). Broken / short-CTC formulas are never force-saved.
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={busy}
            className="rounded-md p-1 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))]"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
          {phase === "ready" ? (
            <div className="overflow-hidden rounded-xl border border-[hsl(var(--ds-border))]">
              <div className="max-h-48 overflow-auto">
                <table className="ds-table w-full text-sm">
                  <thead className="sticky top-0 bg-[hsl(var(--ds-muted))]">
                    <tr>
                      <th className="px-3 py-2 text-left">Emp Code</th>
                      <th className="px-3 py-2 text-left">Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((e) => (
                      <tr key={e.id} className="border-t border-[hsl(var(--ds-border))]">
                        <td className="px-3 py-2 whitespace-nowrap">
                          {e.empCode || e.employeeCode || "—"}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {[e.name || e.firstName || e.user?.name, e.lastName]
                            .filter(Boolean)
                            .join(" ") || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {busy ? (
            <p className="text-sm text-[hsl(var(--ds-fg))]">
              Applying… {progress.done} of {progress.total}
            </p>
          ) : null}

          {phase === "fallback" ? (
            <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/80 px-4 py-3 dark:border-amber-800/50 dark:bg-amber-950/30">
              <p className="text-sm text-[hsl(var(--ds-fg))]">
                <span className="font-semibold">{applied.length}</span> structure(s) saved from
                match.
                {unmatched.length ? (
                  <>
                    {" "}
                    <span className="font-semibold">{unmatched.length}</span> employee(s) have{" "}
                    <strong>no matching structure assignment</strong>. Pick one company Auto
                    (Formula) structure to apply to them (still requires a valid CTC breakdown).
                  </>
                ) : null}
              </p>
              {templatesLoading ? (
                <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">Loading structures…</p>
              ) : (
                <label className="block space-y-1.5">
                  <span className="text-xs font-medium text-[hsl(var(--ds-fg))]">
                    Structure for unmatched employees
                  </span>
                  <select
                    value={pickedId}
                    onChange={(e) => setPickedId(e.target.value)}
                    className="h-9 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 text-sm text-[hsl(var(--ds-fg))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                  >
                    <option value="">Choose a structure…</option>
                    {autoTemplates.length ? (
                      <optgroup label="Auto (Formula) — recommended">
                        {autoTemplates.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name || `Structure ${s.id}`}
                          </option>
                        ))}
                      </optgroup>
                    ) : null}
                    {manualTemplates.length ? (
                      <optgroup label="Manual — needs component import (not CTC-only)">
                        {manualTemplates.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name || `Structure ${s.id}`} (Manual)
                          </option>
                        ))}
                      </optgroup>
                    ) : null}
                  </select>
                </label>
              )}
              {otherAlerts.length ? (
                <StructureApplyAlerts alerts={otherAlerts} title="Still need attention" />
              ) : null}
              {unmatched.length ? (
                <StructureApplyAlerts
                  alerts={unmatched}
                  title="No matching assignment"
                />
              ) : null}
            </div>
          ) : null}

          {phase === "result" ? (
            <>
              <p className="text-sm text-[hsl(var(--ds-fg))]">
                <span className="font-semibold text-[hsl(var(--ds-success))]">
                  {applied.length} structure(s) saved.
                </span>{" "}
                {alerts.length
                  ? `${alerts.length} still need attention.`
                  : "All listed employees now have a structure."}
              </p>
              <StructureApplyAlerts alerts={alerts} title="Not applied" />
            </>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-6 py-3">
          <DsButton type="button" variant="outline" size="sm" onClick={close} disabled={busy}>
            {phase === "ready" ? "Cancel" : "Close"}
          </DsButton>
          {phase === "ready" ? (
            <DsButton
              type="button"
              size="sm"
              onClick={runMatched}
              loading={busy}
              disabled={busy || !employees.length}
            >
              Apply matched structures
            </DsButton>
          ) : null}
          {phase === "fallback" ? (
            <DsButton
              type="button"
              size="sm"
              onClick={runFallback}
              loading={busy}
              disabled={busy || !pickedId || !unmatched.length}
            >
              Apply selected to {unmatched.length} unmatched
            </DsButton>
          ) : null}
        </div>
      </div>
    </div>
  );
}
